/** Tenant brand color persisted for fast re-apply on refresh (server remains source of truth). */
export const BRAND_COLOR_STORAGE_KEY = 'tkm_brand_color';
export const BRAND_DISPLAY_STORAGE_KEY = 'tkm_school_branding';

export const DEFAULT_PRIMARY_HEX = '#4F46E5';
/** Neutral chrome while tenant branding is unresolved (not platform Tamil branding). */
export const NEUTRAL_PENDING_BRAND_HEX = '#94A3B8';

export interface CachedSchoolBranding {
  /** When set, cache applies only to this tenant scope. */
  tenantId?: string;
  logoUrl: string;
  displayName: string;
  displayNameTa: string;
  themeColor: string;
}

function normalizeTenantScope(tenantId: string | null | undefined): string | null {
  const tid = String(tenantId || '').trim();
  if (!tid || tid === 'unknown') return null;
  return tid;
}

/** Legacy cache entries without tenantId are not authoritative for tenant-specific hosts. */
export function isCachedBrandingForTenant(
  branding: CachedSchoolBranding | null | undefined,
  tenantId: string | null | undefined,
): boolean {
  if (!branding) return false;
  const scope = normalizeTenantScope(tenantId);
  if (!scope) return false;
  const cachedTenant = normalizeTenantScope(branding.tenantId);
  if (!cachedTenant) return false;
  return cachedTenant === scope;
}

export interface RgbTriplet {
  r: number;
  g: number;
  b: number;
}

/** Accept `#RRGGBB` (optional leading `#`). */
export function parseHexColor(hex: string): RgbTriplet | null {
  const trimmed = hex.trim();
  const match = /^#?([0-9a-fA-F]{6})$/.exec(trimmed);
  if (!match) return null;

  const value = match[1];
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

export function fallbackPrimary(): string {
  return DEFAULT_PRIMARY_HEX;
}

/** Normalize to `#RRGGBB` uppercase; invalid input falls back to default. */
export function validateThemeColor(hex: string | null | undefined): string {
  if (!hex) return fallbackPrimary();
  const parsed = parseHexColor(hex);
  if (!parsed) return fallbackPrimary();
  const { r, g, b } = parsed;
  const toHex = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Space-separated RGB components for Tailwind `rgb(var(--app-primary) / …)` tokens. */
export function hexToRgb(hex: string): string {
  const parsed = parseHexColor(validateThemeColor(hex));
  if (!parsed) {
    const fallback = parseHexColor(fallbackPrimary())!;
    return `${fallback.r} ${fallback.g} ${fallback.b}`;
  }
  return `${parsed.r} ${parsed.g} ${parsed.b}`;
}

/** Inject `--app-primary` on `document.documentElement`; returns validated hex. */
export function applyBrandTheme(hex: string | null | undefined): string {
  const validHex = validateThemeColor(hex);
  const rgb = hexToRgb(validHex);
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--app-primary', rgb);
  }
  return validHex;
}

export function readCachedBrandColor(): string | null {
  try {
    const cached = localStorage.getItem(BRAND_COLOR_STORAGE_KEY);
    if (!cached) return null;
    return validateThemeColor(cached);
  } catch {
    return null;
  }
}

export function persistBrandColor(hex: string): void {
  try {
    localStorage.setItem(BRAND_COLOR_STORAGE_KEY, validateThemeColor(hex));
  } catch {
    // ignore quota / private mode
  }
}

export function clearBrandColorCache(): void {
  try {
    localStorage.removeItem(BRAND_COLOR_STORAGE_KEY);
    localStorage.removeItem(BRAND_DISPLAY_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function readCachedSchoolBranding(tenantId?: string | null): CachedSchoolBranding | null {
  try {
    const raw = localStorage.getItem(BRAND_DISPLAY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedSchoolBranding>;
    if (
      typeof parsed.logoUrl !== 'string' ||
      typeof parsed.displayName !== 'string' ||
      typeof parsed.displayNameTa !== 'string' ||
      typeof parsed.themeColor !== 'string'
    ) {
      return null;
    }
    const branding: CachedSchoolBranding = {
      tenantId: typeof parsed.tenantId === 'string' ? parsed.tenantId : undefined,
      logoUrl: parsed.logoUrl,
      displayName: parsed.displayName,
      displayNameTa: parsed.displayNameTa,
      themeColor: validateThemeColor(parsed.themeColor),
    };

    const scope = normalizeTenantScope(tenantId);
    if (scope) {
      return isCachedBrandingForTenant(branding, scope) ? branding : null;
    }
    return branding;
  } catch {
    return null;
  }
}

export function persistSchoolBranding(branding: CachedSchoolBranding, tenantId?: string | null): void {
  try {
    const themeColor = validateThemeColor(branding.themeColor);
    const scope = normalizeTenantScope(tenantId ?? branding.tenantId);
    persistBrandColor(themeColor);
    localStorage.setItem(
      BRAND_DISPLAY_STORAGE_KEY,
      JSON.stringify({
        ...branding,
        ...(scope ? { tenantId: scope } : {}),
        themeColor,
      }),
    );
  } catch {
    // ignore quota / private mode
  }
}

/** Apply cached color synchronously to reduce flash before school profile fetch. */
export function applyCachedBrandThemeOnBoot(tenantId?: string | null): string {
  const scope = normalizeTenantScope(tenantId);
  const cachedBranding = readCachedSchoolBranding(scope);
  if (cachedBranding) {
    return applyBrandTheme(cachedBranding.themeColor);
  }
  if (scope) {
    return applyBrandTheme(NEUTRAL_PENDING_BRAND_HEX);
  }
  const cached = readCachedBrandColor();
  return applyBrandTheme(cached ?? fallbackPrimary());
}

export function emptyTenantBrandingShell(): CachedSchoolBranding {
  return {
    logoUrl: '',
    displayName: '',
    displayNameTa: '',
    themeColor: fallbackPrimary(),
  };
}
